import { useCallback, useRef } from 'react';
import { useSelector } from '@xstate/store/react';
import { getHandler } from '../entities/registry';
import type { EntityInteractionContext } from '../entities/EntityKindHandler';
import { useCanvasDragHandlers } from './useCanvasDragHandlers';
import { useCanvasConnectorOps } from './useCanvasConnectorOps';
import { useLockedLayerIds } from './useLockedLayerIds';
import { useCanvasStores } from '../stores/CanvasContext';
import { useEntityData } from '../stores/EntityDataContext';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';

/**
 * Central event delegation hook for all canvas entity interactions.
 * Attaches one set of listeners on a container div instead of per-entity.
 */
export function useEntityEventDelegation() {
  const { store, uiStore } = useCanvasStores();
  const { handleEntityDragStart, handleGroupDragStart, handleWaypointDragStart } = useCanvasDragHandlers();
  const { handleWaypointConnectorMouseDown } = useCanvasConnectorOps();
  const { getRedirectGroupId, handleEntityContextMenu } = useEntityData();
  const lockedLayerIds = useLockedLayerIds();

  // Read editing state reactively so the delegation knows about it
  const editingElementId = useSelector(canvasSelectionStore, s => s.context.editingElementId);

  const hasMovedRef = useRef(false);

  const resolveEntity = useCallback((e: React.SyntheticEvent) => {
    const target = e.target as HTMLElement;
    const entityEl = target.closest('[data-entity-id]') as HTMLElement | null;
    if (!entityEl) return null;
    const entityId = entityEl.dataset.entityId;
    const entityKind = entityEl.dataset.entityKind;
    if (!entityId || !entityKind) return null;
    return { entityId, entityKind, entityEl };
  }, []);

  const buildInteractionContext = useCallback((entityId: string, _entityKind: string): EntityInteractionContext | null => {
    const ctx = store.getSnapshot().context;
    const allEntities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.connectors),
      ...Object.values(ctx.groups),
    ];
    const entity = allEntities.find(e => e.id === entityId);
    if (!entity) return null;
    const redirectGroupId = getRedirectGroupId(entityId);
    const isEditing = editingElementId === entityId;
    const isLocked = lockedLayerIds.has(entity.layerId);
    return {
      entity,
      store,
      uiStore,
      redirectGroupId,
      isEditing,
      isLocked,
      handleEntityDragStart,
      handleGroupDragStart,
      handleWaypointDragStart,
      handleWaypointConnectorMouseDown,
      handleEntityContextMenu,
    };
  }, [store, uiStore, getRedirectGroupId, editingElementId, lockedLayerIds, handleEntityDragStart, handleGroupDragStart, handleWaypointDragStart, handleWaypointConnectorMouseDown, handleEntityContextMenu]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Skip connector dot clicks — they have their own handlers
    if ((e.target as HTMLElement).closest('[data-connector]')) return;
    if (e.button !== 0) return;

    const resolved = resolveEntity(e);
    if (!resolved) return;
    const { entityId, entityKind } = resolved;

    const handler = getHandler(entityKind);
    const interactionCtx = buildInteractionContext(entityId, entityKind);
    if (!interactionCtx) return;
    if (interactionCtx.isLocked) return;

    // Let handler short-circuit (editing mode, outer-half waypoint, resize handle)
    const guardResult = handler.onPointerDownGuard(interactionCtx, e);
    if (guardResult?.handled) return;

    e.stopPropagation();
    hasMovedRef.current = false;

    const startX = e.clientX;
    const startY = e.clientY;
    const shiftKey = e.shiftKey;
    const origEvent = e;
    const redirectGroupId = interactionCtx.redirectGroupId;
    const isWaypoint = entityKind === 'waypoint';

    const handlePointerMove = (ev: PointerEvent) => {
      const canvasEl = (origEvent.target as HTMLElement).closest('[data-canvas-canvas]') as HTMLElement | null;
      const zoom = canvasEl ? parseFloat(canvasEl.dataset.zoom ?? '1') : 1;
      const dx = (ev.clientX - startX) / zoom;
      const dy = (ev.clientY - startY) / zoom;

      if (!hasMovedRef.current && Math.abs(dx) < 3 && Math.abs(dy) < 3) return;

      if (!hasMovedRef.current) {
        hasMovedRef.current = true;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        if (redirectGroupId) {
          handleGroupDragStart(redirectGroupId, origEvent);
        } else if (isWaypoint) {
          handleWaypointDragStart(entityId, origEvent);
        } else {
          handleEntityDragStart(entityId, origEvent);
        }
      }
    };

    const handlePointerUp = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      if (!hasMovedRef.current) {
        const targetId = redirectGroupId ?? entityId;
        if (shiftKey) {
          canvasSelectionStore.send({ type: 'entity.toggle', entityId: targetId });
        } else {
          canvasSelectionStore.send({ type: 'entity.selectOne', entityId: targetId });
        }
        // Post-select behavior from handler
        const clickResult = handler.onEntityClick(interactionCtx, shiftKey);
        if (clickResult?.afterSelect) {
          clickResult.afterSelect();
        }
      }
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [resolveEntity, buildInteractionContext, handleEntityDragStart, handleGroupDragStart, handleWaypointDragStart]);

  const onDoubleClick = useCallback((e: React.MouseEvent) => {
    const resolved = resolveEntity(e);
    if (!resolved) return;
    const { entityId, entityKind } = resolved;

    const handler = getHandler(entityKind);
    const interactionCtx = buildInteractionContext(entityId, entityKind);
    if (!interactionCtx) return;

    // Let handler override (start editing for textblock/textlabel/blocknode)
    const dblResult = handler.onDoubleClick(interactionCtx, e);
    if (dblResult?.handled) {
      e.stopPropagation();
      return;
    }

    // Default: enter group if inside one
    if (interactionCtx.redirectGroupId) {
      e.stopPropagation();
      canvasSelectionStore.send({ type: 'group.enter', groupId: interactionCtx.redirectGroupId });
    }
  }, [resolveEntity, buildInteractionContext]);

  const onContextMenu = useCallback((e: React.MouseEvent) => {
    const resolved = resolveEntity(e);
    if (!resolved) return;
    const { entityId } = resolved;

    const interactionCtx = buildInteractionContext(entityId, resolved.entityKind);
    if (!interactionCtx) return;

    const targetId = interactionCtx.redirectGroupId ?? entityId;
    handleEntityContextMenu(targetId, e);
  }, [resolveEntity, buildInteractionContext, handleEntityContextMenu]);

  return { onPointerDown, onDoubleClick, onContextMenu };
}
